import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppShell from '@/components/feature/AppShell';
import TeacherFilters from '@/pages/dashboard/admin/teachers/components/TeacherFilters';
import TeacherTable from '@/pages/dashboard/admin/teachers/components/TeacherTable';
import TeacherOnboardingModal from '@/pages/dashboard/admin/teachers/components/TeacherOnboardingModal';
import TeacherDetailModal from '@/pages/dashboard/admin/teachers/components/TeacherDetailModal';
import { teachers, teacherRoles, classSections, type TeacherRecord } from '@/mocks/teachers';
import { notifications } from '@/mocks/dashboard';

export default function TeacherListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [teacherData, setTeacherData] = useState<TeacherRecord[]>(teachers);
  const [searchQuery, setSearchQuery] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState<TeacherRecord | null>(null);

  const filteredTeachers = useMemo(() => {
    return teacherData.filter((t) => {
      if (!showInactive && t.status === 'inactive') return false;

      const q = searchQuery.toLowerCase().trim();
      const matchesSearch = !q
        || t.name.toLowerCase().includes(q)
        || t.teacherId.toLowerCase().includes(q)
        || t.role.toLowerCase().includes(q)
        || t.email.toLowerCase().includes(q)
        || t.contactNumber.includes(q);

      const matchesClass = !classFilter || t.className === classFilter;
      const matchesRole = !roleFilter || t.role === roleFilter;

      return matchesSearch && matchesClass && matchesRole;
    });
  }, [teacherData, searchQuery, classFilter, roleFilter, showInactive]);

  const handleOnboard = (data: Partial<TeacherRecord>) => {
    const newTeacher: TeacherRecord = {
      id: `tch-${Date.now()}`,
      teacherId: `TCH-${new Date().getFullYear()}-${String(teacherData.length + 1).padStart(3, '0')}`,
      name: data.name || '',
      dob: data.dob || '',
      role: data.role || '',
      className: data.className || '',
      dateOfJoining: data.dateOfJoining || '',
      contactNumber: data.contactNumber || '',
      email: data.email || '',
      address: data.address || '',
      district: data.district || '',
      town: data.town || '',
      status: (data.status as TeacherRecord['status']) || 'active',
    };
    setTeacherData((prev) => [newTeacher, ...prev]);
  };

  const handleSaveDraft = (data: Partial<TeacherRecord>) => {
    const draftTeacher: TeacherRecord = {
      id: `tch-${Date.now()}`,
      teacherId: `TCH-${new Date().getFullYear()}-${String(teacherData.length + 1).padStart(3, '0')}`,
      name: data.name || 'Unnamed Teacher',
      dob: data.dob || '',
      role: data.role || '',
      className: data.className || '',
      dateOfJoining: data.dateOfJoining || '',
      contactNumber: data.contactNumber || '',
      email: data.email || '',
      address: data.address || '',
      district: data.district || '',
      town: data.town || '',
      status: 'inactive',
    };
    setTeacherData((prev) => [draftTeacher, ...prev]);
  };

  const handleSaveTeacher = (updated: TeacherRecord) => {
    setTeacherData((prev) =>
      prev.map((t) => (t.id === updated.id ? updated : t))
    );
  };

  const handleTeacherStatusChange = (teacher: TeacherRecord, newStatus: string) => {
    setTeacherData((prev) =>
      prev.map((t) => (t.id === teacher.id ? { ...t, status: newStatus as TeacherRecord['status'], deactivatedAt: undefined } : t))
    );
  };

  const activeCount = teacherData.filter((t) => t.status === 'active').length;
  const onLeaveCount = teacherData.filter((t) => t.status === 'on-leave').length;
  const inactiveCount = teacherData.filter((t) => t.status === 'inactive').length;

  return (
    <AppShell
      pageTitle={t('dashboard.facultyManagement')}
      pageSubtitle={t('dashboard.facultySubtitle')}
      breadcrumbs={[{ label: t('common.home'), href: '/dashboard/admin' }, { label: t('dashboard.facultyManagement', { defaultValue: 'Faculty' }) }]}
      notifications={notifications}
    >
      {/* ── Stats Bar ───────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-background-50 rounded-xl border border-background-200 p-4 flex items-center gap-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-semantic-success-bg">
            <i className="ri-checkbox-circle-fill text-lg text-semantic-success" />
          </div>
          <div>
            <p className="text-2xl font-heading font-700 text-foreground-900 leading-none">{activeCount}</p>
            <p className="text-xs font-label text-foreground-500 mt-1">{t('dashboard.activeFaculty')}</p>
          </div>
        </div>
        <div className="bg-background-50 rounded-xl border border-background-200 p-4 flex items-center gap-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-semantic-warning-bg">
            <i className="ri-time-line text-lg text-semantic-warning" />
          </div>
          <div>
            <p className="text-2xl font-heading font-700 text-foreground-900 leading-none">{onLeaveCount}</p>
            <p className="text-xs font-label text-foreground-500 mt-1">{t('dashboard.onLeave')}</p>
          </div>
        </div>
        <div className="bg-background-50 rounded-xl border border-background-200 p-4 flex items-center gap-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-foreground-100">
            <i className="ri-user-unfollow-line text-lg text-foreground-500" />
          </div>
          <div>
            <p className="text-2xl font-heading font-700 text-foreground-900 leading-none">{inactiveCount}</p>
            <p className="text-xs font-label text-foreground-500 mt-1">{t('common.inactive')}</p>
          </div>
        </div>
      </div>

      {/* ── Toolbar ─────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
        <TeacherFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          classFilter={classFilter}
          onClassFilterChange={setClassFilter}
          roleFilter={roleFilter}
          onRoleFilterChange={setRoleFilter}
          showInactive={showInactive}
          onToggleInactive={setShowInactive}
          classOptions={classSections}
          roleOptions={teacherRoles}
        />

        <button
          onClick={() => setOnboardingOpen(true)}
          className="inline-flex items-center gap-2 h-10 px-5 bg-primary-500 text-background-50 text-sm font-label font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-user-add-line" />
          {t('dashboard.addTeacher')}
        </button>
      </div>

      {/* ── Results count ───────────────────────────────────── */}
      <p className="text-xs font-label text-foreground-400 mb-3">
        Showing <span className="font-medium text-foreground-600">{filteredTeachers.length}</span> of{' '}
        <span className="font-medium text-foreground-600">{teacherData.length}</span> teachers
        {(classFilter || roleFilter || searchQuery) && (
          <button
            onClick={() => { setSearchQuery(''); setClassFilter(''); setRoleFilter(''); }}
            className="ml-2 text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
          >
            {t('common.clearAllFilters')}
          </button>
        )}
      </p>

      {/* ── Table ──────────────────────────────────────────── */}
      <TeacherTable
        teachers={filteredTeachers}
        onRowClick={setSelectedTeacher}
        onClassClick={(className) => navigate(`/dashboard/admin/students?class=${encodeURIComponent(className)}`)}
        onStatusChange={handleTeacherStatusChange}
      />

      {/* ── Onboarding Modal ────────────────────────────────── */}
      <TeacherOnboardingModal
        open={onboardingOpen}
        onClose={() => setOnboardingOpen(false)}
        onSubmit={handleOnboard}
        onSaveDraft={handleSaveDraft}
      />

      {/* ── Detail / Edit Modal ─────────────────────────────── */}
      <TeacherDetailModal
        open={!!selectedTeacher}
        teacher={selectedTeacher}
        onClose={() => setSelectedTeacher(null)}
        onSave={handleSaveTeacher}
        onViewStudents={(teacherName) => navigate(`/dashboard/admin/students?teacher=${encodeURIComponent(teacherName)}`)}
      />
    </AppShell>
  );
}